import type { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import Link from "next/link";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Clock3,
  LineChart,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
} from "lucide-react";
import Footer from "pagecomponents/newcomponents/Footer";
import Navbar from "pagecomponents/newcomponents/Navbar";
import SeoCtaLink from "@/components/SeoCtaLink";
import {
  comparisonPages,
  getComparisonBySlug,
  type ComparisonPage,
} from "@/src/comparisons";
import { FREE_TRIAL_APPLICATION_CREDITS } from "@src/config/freeTrial";
import styles from "./style.module.css";

type ComparePageProps = {
  page: ComparisonPage;
  relatedPages: ComparisonPage[];
};

const siteUrl = "https://www.lifeshack.com";

function trialHref(slug: string) {
  return `/signup/?utm_source=seo&utm_medium=organic&utm_campaign=alternatives&utm_content=${slug}`;
}

export default function ComparePage({ page, relatedPages }: ComparePageProps) {
  const canonicalUrl = `${siteUrl}/compare/${page.slug}/`;
  const signupHref = trialHref(page.slug);
  const pageName = `LifeShack vs ${page.competitor}`;

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: siteUrl,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Compare",
        item: `${siteUrl}/compare/`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: pageName,
        item: canonicalUrl,
      },
    ],
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <div className={styles.page}>
      <Head>
        <title>{page.title}</title>
        <meta name="description" content={page.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${siteUrl}/widget.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.title} />
        <meta name="twitter:description" content={page.metaDescription} />
        <meta name="twitter:image" content={`${siteUrl}/widget.png`} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbJsonLd),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      </Head>

      <Navbar />

      <main>
        <section className={styles.hero}>
          <div className={styles.heroInner}>
            <p className={styles.eyebrow}>{page.eyebrow}</p>
            <h1>{page.headline}</h1>
            <p className={styles.subheadline}>{page.subheadline}</p>
            <p className={styles.researchDisclosure}>
              Published by LifeShack, one of the products compared. Based on official
              sources{page.reviewedAt ? ` reviewed ${page.reviewedAt}` : ""}, not a
              hands-on product test.
            </p>
            <div className={styles.heroActions}>
              <SeoCtaLink
                href={signupHref}
                pageType="comparison"
                placement="hero"
                competitorSlug={page.slug}
                className={styles.primaryCta}
              >
                Start with free application credits <ArrowRight size={18} />
              </SeoCtaLink>
              <a className={styles.secondaryCta} href="#comparison">
                Compare features
              </a>
            </div>
            <div className={styles.trialProof} aria-label="Introductory offer details">
              <span>
                <CheckCircle2 size={16} /> {FREE_TRIAL_APPLICATION_CREDITS} application credits included
              </span>
              <span>
                <CheckCircle2 size={16} /> No credit card required
              </span>
              <span>
                <CheckCircle2 size={16} /> Built for active job searches
              </span>
            </div>
          </div>
        </section>

        <section className={styles.sectionAlt}>
          <div className={styles.sectionInner}>
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Quick verdict</p>
              <h2>Which workflow fits the way you want to apply?</h2>
              <p>
                Here is the practical difference between LifeShack and{" "}
                {page.competitor}, based on how much of the application work
                you want handled for you.
              </p>
            </div>

            <div className={styles.summaryGrid}>
              <article className={styles.summaryPanel}>
                <div className={styles.summaryIcon}>
                  <Search size={22} />
                </div>
                <h3>Choose {page.competitor} when…</h3>
                <p>{page.competitorSummary}</p>
                <div className={styles.bestForBox}>
                  <span>Best if</span>
                  <strong>{page.competitorBestFor}</strong>
                </div>
                <h4>Reasons to consider {page.competitor}</h4>
                <ul>
                  {page.competitorStrengths.map((strength) => (
                    <li key={strength}>
                      <CheckCircle2 size={17} /> {strength}
                    </li>
                  ))}
                </ul>
                {page.competitorLimitation ? (
                  <p><strong>Limit to consider:</strong> {page.competitorLimitation}</p>
                ) : null}
              </article>

              <article className={styles.summaryPanel}>
                <div className={`${styles.summaryIcon} ${styles.summaryIconAlt}`}>
                  <Sparkles size={22} />
                </div>
                <div className={styles.fitBadge}>
                  Our product
                </div>
                <h3>Choose LifeShack when…</h3>
                <p>{page.lifeshackSummary}</p>
                <div className={styles.bestForBox}>
                  <span>Best if</span>
                  <strong>{page.lifeshackBestFor}</strong>
                </div>
                <h4>Reasons to consider LifeShack</h4>
                <ul>
                  {page.lifeshackAdvantages.map((advantage) => (
                    <li key={advantage}>
                      <CheckCircle2 size={17} /> {advantage}
                    </li>
                  ))}
                </ul>
                {page.lifeshackLimitation ? (
                  <p><strong>Limit to consider:</strong> {page.lifeshackLimitation}</p>
                ) : null}
              </article>
            </div>

            {page.pricingNote ? (
              <div className={styles.verdictPanel}>
                <div>
                  <p className={styles.eyebrow}>
                    Pricing note{page.reviewedAt ? ` reviewed ${page.reviewedAt}` : ""}
                  </p>
                  <h3>{page.pricingNote}</h3>
                  {page.sources?.[0] ? (
                    <a
                      href={page.sources[0].href}
                      rel="nofollow noopener noreferrer"
                      target="_blank"
                    >
                      Verify on {page.sources[0].label}
                    </a>
                  ) : (
                    <p>We could not verify a current public pricing source.</p>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <section className={styles.section} id="comparison">
          <div className={styles.sectionInner}>
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Feature-by-feature comparison</p>
              <h2>{pageName}: which one fits your job search?</h2>
              <p>
                If your goal is more completed applications with less repetitive
                work, compare the workflow instead of only the feature list.
              </p>
            </div>

            <div className={styles.comparisonTable} role="table">
              <div className={styles.tableHeader} role="row">
                <div role="columnheader">Category</div>
                <div role="columnheader">{page.competitor}</div>
                <div role="columnheader">LifeShack</div>
              </div>
              {page.features.map((feature) => (
                <div className={styles.tableRow} role="row" key={feature.feature}>
                  <div
                    className={`${styles.tableCell} ${styles.featureName}`}
                    role="cell"
                  >
                    <strong>{feature.feature}</strong>
                  </div>
                  <div className={styles.tableCell} role="cell">
                    <span className={styles.mobileColumnLabel}>
                      {page.competitor}
                    </span>
                    {feature.competitor}
                  </div>
                  <div
                    className={`${styles.tableCell} ${styles.lifeshackCell}`}
                    role="cell"
                  >
                    <span className={styles.mobileColumnLabel}>LifeShack</span>
                    {feature.lifeshack}
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.verdictPanel}>
              <div>
                <p className={styles.eyebrow}>Bottom line</p>
                <h3>{page.verdict}</h3>
              </div>
              <SeoCtaLink
                href={signupHref}
                pageType="comparison"
                placement="verdict"
                competitorSlug={page.slug}
                className={styles.smallCta}
              >
                Try LifeShack free <ArrowRight size={17} />
              </SeoCtaLink>
            </div>
          </div>
        </section>

        {page.analysisSections?.map((section, index) => (
          <section
            className={index % 2 === 0 ? styles.sectionAlt : styles.section}
            key={section.heading}
          >
            <div className={styles.sectionInner}>
              <div className={styles.sectionHeader}>
                <p className={styles.eyebrow}>What this means in practice</p>
                <h2>{section.heading}</h2>
              </div>
              <div className={styles.faqGrid}>
                {section.body.map((paragraph) => (
                  <article className={styles.faqItem} key={paragraph}>
                    <p>{paragraph}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        ))}

        <section className={styles.sectionAlt}>
          <div className={styles.sectionInner}>
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>Where LifeShack fits</p>
              <h2>
                A stronger {page.competitor} alternative when you want
                applications moving, not just another task list.
              </h2>
              <p>
                LifeShack is built around the moment when a job seeker is ready
                to stop researching and start applying.
              </p>
              <p>
                Explore{" "}
                <Link href="/ai-auto-apply/">
                  <a className={styles.inlineLink}>LifeShack’s AI auto-apply workflow</a>
                </Link>
                , from job preferences to application submission.
              </p>
            </div>

            <div className={styles.searchIntentGrid}>
              <article className={styles.intentItem}>
                <Target size={24} />
                <h3>For active applicants</h3>
                <p>
                  Turn saved roles, resumes, and preferences into a repeatable
                  application pipeline with less manual clicking.
                </p>
              </article>
              <article className={styles.intentItem}>
                <ShieldCheck size={24} />
                <h3>For quality and control</h3>
                <p>
                  Review where your applications are going while LifeShack keeps
                  the workflow moving in the background.
                </p>
              </article>
              <article className={styles.intentItem}>
                <Clock3 size={24} />
                <h3>For proof before paying</h3>
                <p>
                  Start with {FREE_TRIAL_APPLICATION_CREDITS} application
                  credits and judge the platform by whether it creates real
                  application momentum for your search.
                </p>
              </article>
              <article className={styles.intentItem}>
                <LineChart size={24} />
                <h3>For more application momentum</h3>
                <p>
                  Use LifeShack when your biggest bottleneck is not finding one
                  more tool, but getting more relevant applications submitted.
                </p>
              </article>
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionInner}>
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>FAQs</p>
              <h2>Questions job seekers ask before switching.</h2>
            </div>
            <div className={styles.faqGrid}>
              {page.faqs.map((faq) => (
                <article className={styles.faqItem} key={faq.question}>
                  <h3>{faq.question}</h3>
                  <p>{faq.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {page.sources?.length ? (
          <section className={styles.sectionAlt}>
            <div className={styles.sectionInner}>
              <div className={styles.sectionHeader}>
                <p className={styles.eyebrow}>Research sources</p>
                <h2>Public pages checked for this comparison.</h2>
                <p>
                  Competitor pricing, plan limits, and feature names can change.
                  These source links help readers verify the current vendor
                  claims behind this comparison.
                </p>
              </div>
              <div className={styles.hubGrid}>
                {page.sources.map((source) => (
                  <a
                    className={styles.hubCard}
                    href={source.href}
                    key={source.href}
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                  >
                    <span>{page.competitor}</span>
                    <h3>{source.label}</h3>
                    <strong>
                      Visit source <ChevronRight size={16} />
                    </strong>
                  </a>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        <section className={styles.ctaBand}>
          <div className={styles.ctaInner}>
            <div>
              <p className={styles.eyebrow}>Start applying today</p>
              <h2>See whether LifeShack fits your application workflow.</h2>
              <p>
                Start with {FREE_TRIAL_APPLICATION_CREDITS} free application
                credits, connect your job-search preferences,
                and let the platform move more applications forward.
              </p>
            </div>
            <SeoCtaLink
              href={signupHref}
              pageType="comparison"
              placement="bottom_cta"
              competitorSlug={page.slug}
              className={styles.primaryCta}
            >
              Start with free credits <ArrowRight size={18} />
            </SeoCtaLink>
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.sectionInner}>
            <div className={styles.sectionHeader}>
              <p className={styles.eyebrow}>More comparisons</p>
              <h2>Keep comparing AI job search tools.</h2>
            </div>
            <div className={styles.hubGrid}>
              <Link href={`/alternatives/${page.slug}/`}>
                <a className={styles.hubCard}>
                  <span>Alternatives guide</span>
                  <h3>7 best {page.competitor} alternatives</h3>
                  <p>
                    Compare application agents, autofill tools, trackers, and
                    managed services by workflow and best-fit use case.
                  </p>
                  <strong>
                    Explore alternatives <ChevronRight size={16} />
                  </strong>
                </a>
              </Link>
              <Link href="/best-ai-auto-apply-tools/">
                <a className={styles.hubCard}>
                  <span>Category guide</span>
                  <h3>Best AI auto-apply tools</h3>
                  <p>
                    Understand the difference between browser extensions,
                    trackers, browser bots, and cloud application agents.
                  </p>
                  <strong>
                    View category guide <ChevronRight size={16} />
                  </strong>
                </a>
              </Link>
              {relatedPages.map((relatedPage) => (
                <Link
                  href={`/compare/${relatedPage.slug}/`}
                  key={relatedPage.slug}
                >
                  <a className={styles.hubCard}>
                    <span>{relatedPage.eyebrow}</span>
                    <h3>LifeShack vs {relatedPage.competitor}</h3>
                    <p>{relatedPage.metaDescription}</p>
                    <strong>
                      Read comparison <ChevronRight size={16} />
                    </strong>
                  </a>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <SeoCtaLink
          href={signupHref}
          pageType="comparison"
          placement="mobile_sticky"
          competitorSlug={page.slug}
          className={styles.mobileTrial}
        >
          Start with free credits <ArrowRight size={17} />
        </SeoCtaLink>
      </main>

      <Footer />
    </div>
  );
}

ComparePage.isPublicSeoPage = true;

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: comparisonPages.map((page) => ({
    params: { slug: page.slug },
  })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<ComparePageProps> = async ({
  params,
}) => {
  const slug = typeof params?.slug === "string" ? params.slug : "";
  const page = getComparisonBySlug(slug);

  if (!page) {
    return {
      notFound: true,
    };
  }

  const prioritizedRelatedPages = (page.relatedComparisonSlugs ?? [])
    .map(getComparisonBySlug)
    .filter(
      (relatedPage): relatedPage is ComparisonPage =>
        relatedPage !== undefined && relatedPage.slug !== page.slug
    );
  const prioritizedSlugs = new Set(
    prioritizedRelatedPages.map((relatedPage) => relatedPage.slug)
  );
  const fallbackRelatedPages = comparisonPages.filter(
    (comparisonPage) =>
      comparisonPage.slug !== page.slug &&
      !prioritizedSlugs.has(comparisonPage.slug)
  );

  return {
    props: {
      page,
      relatedPages: [
        ...prioritizedRelatedPages,
        ...fallbackRelatedPages,
      ].slice(0, 3),
    },
  };
};
